/**
 * Sprint 5.bis.4 — Wow moment confetti.
 *
 * 60 partículas CSS (TOKENS §10 — "Confeti del momento wow: 60 partículas,
 * fade 1.4s, sin sonido"). Paleta curada de 4 colores (TOKENS §1 + §2):
 *
 *   Oro suave      #D4AF37   (celebración)
 *   Blanco perla   #F0EAD6   (luminoso)
 *   Primary VERIX  #2563EB   (marca)
 *   Success        #16A34A   (semántico — operación completada)
 *
 * Sin sonido, `pointer-events: none` en el wrapper, z-index alto pero NO
 * bloquea interacción del contenido. Respeta `@media (prefers-reduced-
 * motion: no-preference)` (TOKENS §8): usuarios con motion reducido NO
 * ven el confetti — el check icon + texto del Wow ya transmite el éxito.
 *
 * Trigger una sola vez por montaje: `firedRef` previene re-fire si el
 * padre vuelve a renderizar con el mismo `trigger=true`. Cleanup del
 * timer en unmount evita memory leaks si el user navega antes.
 *
 * Drift lateral: cada partícula tiene un `--drift` CSS variable
 * random(-30, 30)px que el keyframe interpola en `translateX`, dando
 * movimiento natural sinusoidal en lugar de caída vertical recta.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

const PARTICLE_COUNT = 60;
const FADE_DURATION_MS = 1400;

// 4-color palette cycled via index modulo. Hex values from TOKENS §1/§2.
const PALETTE = ['#D4AF37', '#F0EAD6', '#2563EB', '#16A34A'];

function makeParticles(count) {
    const arr = [];
    for (let i = 0; i < count; i++) {
        arr.push({
            id: i,
            color: PALETTE[i % PALETTE.length],
            // Horizontal start position 0-100% of viewport.
            leftPct: Math.random() * 100,
            // Lateral drift -30..+30 px during the fall.
            driftPx: Math.round((Math.random() - 0.5) * 60),
            // Stagger start 0-300ms so the burst feels organic, not synchronized.
            delayMs: Math.round(Math.random() * 300),
            // Slight size variation (4-7px).
            sizePx: 4 + Math.round(Math.random() * 3),
            // Rotation total degrees during the fall — varied per particle.
            rotateDeg: Math.round(360 + Math.random() * 720),
        });
    }
    return arr;
}

export default function ConfettiBurst({ trigger, particleCount = PARTICLE_COUNT, className }) {
    const firedRef = useRef(false);
    const timerRef = useRef(null);
    const [visible, setVisible] = useState(false);

    // Generate particles ONCE per mount — re-creating them on every render
    // would defeat the random distribution. Memo key is the count so the
    // burst is stable across re-renders.
    const particles = useMemo(() => makeParticles(particleCount), [particleCount]);

    useEffect(() => {
        if (!trigger || firedRef.current) return;
        firedRef.current = true;
        setVisible(true);
        timerRef.current = setTimeout(() => {
            setVisible(false);
        }, FADE_DURATION_MS + 400); // small grace so the last delayed particle finishes its fade
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [trigger]);

    // Unmount cleanup — independent from the trigger effect so navigation
    // away during the burst doesn't leak the timer.
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, []);

    if (!visible) return null;

    return (
        <div
            className={cn(
                // pointer-events-none keeps the layer transparent to clicks
                // so the CTAs underneath stay interactive.
                'pointer-events-none fixed inset-0 z-50 overflow-hidden motion-reduce:hidden',
                className,
            )}
            aria-hidden="true"
            data-testid="confetti-burst"
        >
            {particles.map((p) => (
                <span
                    key={p.id}
                    className="confetti-particle absolute top-0 block rounded-sm"
                    style={{
                        left: `${p.leftPct}%`,
                        width: `${p.sizePx}px`,
                        height: `${p.sizePx}px`,
                        backgroundColor: p.color,
                        // CSS vars consumed by the @keyframes in index.css.
                        '--confetti-drift': `${p.driftPx}px`,
                        '--confetti-rotate': `${p.rotateDeg}deg`,
                        '--confetti-delay': `${p.delayMs}ms`,
                    }}
                />
            ))}
        </div>
    );
}
